import { Building2 } from 'lucide-react';
import { useEffect } from 'react';
import { useBranchStore } from '@/stores';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * iter880 — Shared BranchFilter dropdown. Renders all tenant branches; emits
 * `null` for "All branches" and a branch id otherwise. Uses the global
 * branchStore so it stays in sync with the top-level branch picker.
 *
 * Props:
 *   value: string|null
 *   onChange: fn(branch_id|null)
 *   includeAll: bool (defaults true — shows "All branches" option)
 *   width: css width class (defaults 'w-40')
 *   testid: string (prefix for data-testids)
 */
export function BranchFilter({ value, onChange, includeAll = true, width = 'w-40', testid = 'branch-filter' }) {
  const branches = useBranchStore((s) => s.branches);
  const fetchBranches = useBranchStore((s) => s.fetchBranches);
  // Auto-fetch so the dropdown is never empty (e.g. on pages that don't
  // already prime the branch store).
  useEffect(() => {
    if (!branches || branches.length === 0) fetchBranches?.();
  }, []);
  const selectValue = value == null || value === '' ? '__all__' : value;
  const handle = (v) => onChange(v === '__all__' ? null : v);
  return (
    <Select value={selectValue} onValueChange={handle}>
      <SelectTrigger className={`${width} h-10 rounded-xl gap-1`} data-testid={`${testid}-trigger`}>
        <div className="flex items-center gap-1.5 min-w-0">
          <Building2 size={14} className="text-muted-foreground shrink-0" />
          <SelectValue placeholder="Branch" />
        </div>
      </SelectTrigger>
      <SelectContent>
        {includeAll && <SelectItem value="__all__" data-testid={`${testid}-all`}>All branches</SelectItem>}
        {branches.map((b) => (
          <SelectItem key={b.id} value={b.id} data-testid={`${testid}-option-${b.id}`}>
            {b.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default BranchFilter;
